import { Slider, SxProps } from '@mui/material';

import { LegendType } from 'appTypes';
import Color from 'colorjs.io';
import {
  ColorGradient,
  format_color_for_mapbox,
} from 'features/chart/colorMaps';
import { LegendStops } from 'features/workflow_timeline/metricsOptions';
import { null_grey } from 'theme/cemTheme';

import styles from './LegendSlider.module.css';

// Remaps a stop value to an evenly distributed position in the [min_val, max_val] range.
const remapX = (value, evenStops, stops_values, min_val, max_val) => {
  if (evenStops) {
    let index;
    const max_index = stops_values.length - 1;
    index = stops_values.indexOf(value);
    if (index === -1) {
      for (let i = 0; i < stops_values.length; i++) {
        if (value < stops_values[i]) {
          if (i === 0) {
            return 0;
          } else {
            const stop_before = stops_values[i - 1];
            const stop_after = stops_values[i];
            index = i - 1 + (value - stop_before) / (stop_after - stop_before);
            break;
          }
        }
      }
      if (index === undefined) {
        index = max_index;
      }
    }
    return min_val + (max_val - min_val) * (index / max_index);
  } else {
    return value;
  }
};

export function buildDiscreteBackground(colors) {
  const gapPercent = 0.4;

  // Get the list of colors, but ignore the last one which is for infinit values
  const filteredColors = colors.filter(
    (_, idx) => idx % 2 === 1 && idx < colors.length - 1,
  );

  const numBlocks = filteredColors.length;
  if (numBlocks === 0) {
    return 'linear-gradient(0.25turn, transparent 0%, transparent 100%)';
  }

  const totalGap = gapPercent * Math.max(0, numBlocks - 1);
  const blockWidth = (100 - totalGap) / numBlocks;

  const segments: string[] = [];
  let currentPosition = 0;

  const nullGreyColor = new Color(null_grey);

  const mixColor = (color) => {
    // Mix color against null grey to give opaque color with similar appearance to metric color on top of map segment
    const colorColor = new Color(color);
    return format_color_for_mapbox(
      nullGreyColor.mix(colorColor, colorColor.alpha),
    );
  };

  for (let i = 0; i < numBlocks; i++) {
    const color = filteredColors[i];

    // Block span
    const blockStart = currentPosition;
    const blockEnd = blockStart + blockWidth;

    const mixed_color = mixColor(color);

    segments.push(`${mixed_color} ${blockStart.toFixed(6)}%`);
    segments.push(`${mixed_color} ${blockEnd.toFixed(6)}%`);

    // Gap after block (not after the last)
    const isLast = i === numBlocks - 1;
    if (!isLast) {
      const gapStart = blockEnd;
      const gapEnd = gapStart + gapPercent;
      segments.push(`transparent ${gapStart.toFixed(6)}%`);
      segments.push(`transparent ${gapEnd.toFixed(6)}%`);
      currentPosition = gapEnd;
    } else {
      currentPosition = blockEnd;
      // The last block sometimes might have a gradient color (Queuing module)
      segments.pop();
      const freeflowColor = colors[colors.length - 1]; // Last color in the array
      const gradientEnd = 100;

      segments.push(`${mixColor(freeflowColor)} ${gradientEnd.toFixed(6)}%`);
    }
  }

  return `linear-gradient(0.25turn, ${segments.join(', ')})`;
}

export function buildContinuousBackground(
  stops_values,
  min_val,
  max_val,
  colors,
) {
  const colormap = new ColorGradient(colors);
  let legendBackground = 'linear-gradient(0.25turn';
  for (let i = 0; i < colormap.stop_colors.length; i++) {
    const stop = colormap.stop_colors[i];
    const perc =
      100 *
      ((remapX(stop.stop, false, stops_values, min_val, max_val) - min_val) /
        (max_val - min_val));
    // @ts-ignore
    legendBackground += `, ${stop.color.toString({ format: 'hex' })} ${perc.toFixed(3)}%`;
  }
  legendBackground += ')';
  return legendBackground;
}

// Build marks depending if there's a special case with Typical
function buildDiscreteMarks(stops, min, max) {
  const n = stops.length;
  const getLabels = (s) =>
    Array.isArray(s.label)
      ? { desc: s.label[0], numeric: s.label[1] }
      : { desc: '', numeric: s.label };

  // Detect if there's a Typical stop
  const typicalIndex = stops.findIndex((s) => {
    const { numeric } = getLabels(s);
    return typeof numeric === 'string' && numeric.toLowerCase() === 'typical';
  });

  const borderCount = typicalIndex !== -1 ? n - 1 : n;

  const indexToValue = (i) => {
    const t = i / (borderCount - 1);
    return min + t * (max - min);
  };

  const marks = [];
  let compressedIndex = 0;

  // Push all marks except the Typical,
  // for each non-typical stop compute it's value
  // and render label only when is not empty (we don't want marks for 25 and -25)
  for (let i = 0; i < n; i++) {
    if (i !== typicalIndex) {
      const { desc, numeric } = getLabels(stops[i]);
      const hasNumeric =
        typeof numeric === 'string' && numeric.trim().length > 0;

      marks.push({
        value: indexToValue(compressedIndex),
        label: hasNumeric && (
          <>
            <span className={styles.map_legend_upper_label}>{numeric}</span>
            <span className={styles.map_legend_lower_label}>{desc}</span>
          </>
        ),
      });
      compressedIndex++;
    }
  }

  // Special case for typical value where we need to calculate the value
  // for the centered space over the block
  if (typicalIndex !== -1) {
    const leftCompressed = typicalIndex - 1 >= 0 ? typicalIndex - 1 : 0;
    const rightCompressed = typicalIndex + 1 < n ? typicalIndex + 1 : n - 1;

    // Center position in compressed space
    const centerIndex = (leftCompressed + rightCompressed - 1) / 2;
    const centerValue = indexToValue(centerIndex);

    const { desc, numeric } = getLabels(stops[typicalIndex]);

    marks.push({
      value: centerValue,
      label: (
        <>
          <span
            className={styles.map_legend_upper_label}
            title="Typical range: −25 … 25"
          >
            {numeric}
          </span>
          <span className={styles.map_legend_lower_label}>{desc}</span>
        </>
      ),
    });
  }

  return marks;
}

function buildContinuousMarks(stops, min, max) {
  const stops_values: number[] = Array.from(
    new Set([...stops.map((stop) => stop.value), min, max]),
  );
  stops_values.sort((a, b) => a - b);

  const marks = stops.map((stop, i) => {
    const numeric = stop.label[1];
    const desc = stop.label[0];
    return {
      value: remapX(stop.value, false, stops_values, min, max),
      label: (
        <>
          <span className={styles.map_legend_upper_label}>{numeric}</span>
          <span className={styles.map_legend_lower_label}>{desc}</span>
        </>
      ),
    };
  });
  return marks;
}

function getMarks(legendType, stops, min, max) {
  return legendType === 'discrete'
    ? buildDiscreteMarks(stops, min, max)
    : buildContinuousMarks(stops, min, max);
}

export function LegendSlider({
  colormap,
  stops,
  legendType,
  displayMarks = false,
  legendSx = {},
  className,
}: {
  colormap: any;
  stops: LegendStops;
  legendType: LegendType;
  displayMarks?: boolean;
  legendSx?: SxProps;
  className?: string;
}) {
  const cmap_stops = stops.map((s) => s.value);
  const min = Math.min(...cmap_stops);
  const max = Math.max(...cmap_stops);
  const marks = displayMarks ? getMarks(legendType, stops, min, max) : [];

  const background =
    legendType === 'discrete'
      ? buildDiscreteBackground(colormap)
      : buildContinuousBackground(cmap_stops, min, max, colormap);

  const sx = {
    ...legendSx,
    '.MuiSlider-rail': {
      ...legendSx['.MuiSlider-rail'],
      background,
    },
  };

  return (
    <Slider
      valueLabelDisplay="off"
      min={min}
      max={max}
      color="secondary"
      marks={marks}
      sx={sx}
      className={className}
    />
  );
}
